/**
 * Reduce dense sensor readings for chart display. Averaging within time buckets
 * prevents high-frequency oscillation from rendering as a solid filled band.
 *
 * @param {Array<{date: number, value: number}>} points
 * @param {number} maxPoints
 * @return {Array<{date: number, value: number}>}
 */
export function simplifyChartPoints(points, maxPoints = 600) {
    if (!points.length || points.length <= maxPoints) {
        return points;
    }

    const startMs = points[0].date;
    const endMs = points[points.length - 1].date;
    const span = endMs - startMs;

    if (span <= 0) {
        return [points[0]];
    }

    const bucketMs = span / maxPoints;
    const simplified = [];
    let bucketIndex = 0;
    let sum = 0;
    let count = 0;
    let lastDate = points[0].date;

    for (const point of points) {
        const targetBucket = Math.min(
            maxPoints - 1,
            Math.floor((point.date - startMs) / bucketMs),
        );

        if (targetBucket > bucketIndex && count > 0) {
            simplified.push({
                date: lastDate,
                value: sum / count,
            });
            bucketIndex = targetBucket;
            sum = 0;
            count = 0;
        }

        sum += point.value;
        count += 1;
        lastDate = point.date;
    }

    if (count > 0) {
        simplified.push({
            date: lastDate,
            value: sum / count,
        });
    }

    return simplified;
}

/**
 * Scale point budget to the visible time span so zoomed views stay readable.
 *
 * @param {number} visibleSpanMs
 * @param {number} fullSpanMs
 * @param {number} fullMaxPoints
 * @return {number}
 */
export function maxChartPointsForSpan(visibleSpanMs, fullSpanMs, fullMaxPoints = 600) {
    if (fullSpanMs <= 0 || visibleSpanMs <= 0) {
        return fullMaxPoints;
    }

    const ratio = Math.min(1, visibleSpanMs / fullSpanMs);

    return Math.max(80, Math.round(fullMaxPoints * Math.max(0.12, ratio)));
}

/**
 * @param {Array<{date: number, value: number}>} points
 * @param {number|null} startMs
 * @param {number|null} endMs
 * @param {number} maxPoints
 * @return {Array<{date: number, value: number}>}
 */
export function simplifyChartPointsForRange(points, startMs, endMs, maxPoints = 600) {
    if (startMs == null || endMs == null) {
        return simplifyChartPoints(points, maxPoints);
    }

    const visible = points.filter((point) => point.date >= startMs && point.date <= endMs);

    if (!visible.length) {
        return simplifyChartPoints(points, maxPoints);
    }

    return simplifyChartPoints(visible, maxPoints);
}
